"use client";

import { cn } from "../lib/utils";
import { useCartAssistant } from "../lib/cartAssistant/CartAssistantProvider";
import { Loader2Icon, ChevronDown } from "lucide-react";
import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from 'react-i18next';

export interface GenerationStageProps extends React.HTMLAttributes<HTMLDivElement> {
  showLabel?: boolean;
}

export function MessageGenerationStage({
  className,
  showLabel = true,
}: GenerationStageProps) {
  const { t } = useTranslation();
  const { streamingState, isWaiting, isStreaming, thread } = useCartAssistant();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const status = streamingState?.status;

  if (!isWaiting && !isStreaming && (!status || status === "idle")) {
    return null;
  }

  const messages = thread?.thread?.messages ?? [];
  const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;

  // Tier 1: live reasoning chunks from the model
  const reasoningText = lastMsg?.reasoning?.join('') ?? '';

  // Tier 2: active (incomplete) tool calls
  const activeTools: string[] = lastMsg?.role === 'assistant' && Array.isArray(lastMsg.content)
    ? lastMsg.content
        .filter((c: any) => c.type === 'tool_use' && !c.hasCompleted)
        .map((c: any) => c.name as string)
    : [];

  const hasExpandableContent = reasoningText.length > 0 || activeTools.length > 0;

  const stageLabels: Record<string, string> = {
    idle: t('chat.idle'),
    waiting: t('chat.thinking'),
    streaming: t('chat.generating_response'),
  };

  const label = status ? (stageLabels[status] ?? status) : t('chat.thinking');

  // "process_uploaded_image" → "Processing uploaded image"
  const formatToolName = (name: string) =>
    name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

  return (
    <motion.div
      layout
      className={cn(
        "inline-flex flex-col bg-gray-100/80 rounded-[1.5rem] rounded-bl-md overflow-hidden",
        className
      )}
    >
      {/* Header row — always visible */}
      <button
        onClick={() => hasExpandableContent && setIsExpanded(v => !v)}
        className={cn(
          "flex items-center gap-2.5 px-4 py-3 text-gray-500 font-semibold text-[12px]",
          hasExpandableContent ? "cursor-pointer" : "cursor-default"
        )}
      >
        <span className="flex items-center gap-1 shrink-0" aria-hidden="true">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bounce [animation-delay:-0.3s]" />
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bounce [animation-delay:-0.15s]" />
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bounce" />
        </span>
        {showLabel && <span>{activeTools.length > 0 ? formatToolName(activeTools[activeTools.length - 1]) : label}</span>}
        {hasExpandableContent && (
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="ml-0.5 flex items-center"
          >
            <ChevronDown size={12} strokeWidth={3} />
          </motion.span>
        )}
      </button>

      {/* Expandable panel */}
      <AnimatePresence initial={false}>
        {isExpanded && hasExpandableContent && (
          <motion.div
            key="expand"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 pt-1 max-h-44 overflow-y-auto border-t border-gray-200/60">
              {reasoningText ? (
                <p className="text-[10px] text-gray-400 font-medium leading-relaxed whitespace-pre-wrap">
                  {reasoningText}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {activeTools.map((name, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Loader2Icon size={9} className="animate-spin text-emerald-400 shrink-0" />
                      <span className="text-[11px] text-gray-500 font-medium">
                        {formatToolName(name)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
